<template>
  <Teleport v-if="!isHidden" to="body">
    <div v-bind="$attrs"
         :id="ids.outer"
         :style="{ position: 'absolute', zIndex: managed.zIndex }">
      <Transition enter-from-class=""
                  enter-active-class=""
                  enter-to-class=""
                  leave-from-class=""
                  leave-active-class=""
                  leave-to-class=""
                  @before-enter="onBeforeEnter"
                  @enter="onEnter"
                  @after-enter="onAfterEnter"
                  @before-leave="onBeforeLeave"
                  @leave="onLeave"
                  @after-leave="onAfterLeave">
        <div v-show="isVisible"
             class="modal fade"
             :class="[{ show: isShow }, modalClass]"
             :id="ids.modal"
             role="dialog"
             :aria-labelledby="hasTitle ? ids.title : null"
             :aria-describedby="ids.body"
             :aria-hidden="isVisible ? null : 'true'"
             :aria-modal="isVisible ? 'true' : null"
             :style="modalStyles"
             ref="modal"
             @keydown="onEsc"
             @click="onClickOut">
          <div class="modal-dialog"
               :class="[`modal-${size}`, { 'modal-dialog-centered': centered }]"
               @mousedown="onDialogMousedown">
            <span v-if="isVisible && !noEnforceFocus" tabindex="0" ref="topTrap"></span>
            <div class="modal-content"
                 :class="contentClass"
                 :id="ids.content"
                 tabindex="-1"
                 ref="content">
              <header v-if="!hideHeader"
                      class="modal-header"
                      :class="headerClass"
                      :id="ids.header">
                <h5 v-if="$slots['modal-title']"
                    class="modal-title"
                    :class="titleClass"
                    :id="ids.title"><slot name="modal-title"/></h5>
                <h5 v-else
                    class="modal-title"
                    :class="titleClass"
                    :id="ids.title">{{ title }}</h5>
                <button v-if="!hideHeaderClose"
                        type="button"
                        :disabled="isTransitioning"
                        aria-label="Close"
                        class="close"
                        @click="onClose">×</button>
              </header>
              <div class="modal-body"
                   :class="bodyClass"
                   :id="ids.body">
                <slot/>
              </div>
              <footer v-if="!hideFooter"
                      class="modal-footer"
                      :class="footerClass"
                      :id="ids.footer">
                <slot name="modal-footer" :close="onClose"/>
              </footer>
            </div>
            <span v-if="isVisible && !noEnforceFocus" tabindex="0" ref="bottomTrap"></span>
          </div>
        </div>
      </Transition>
      <Transition enter-from-class=""
                  enter-active-class="fade"
                  enter-to-class="show"
                  leave-from-class=""
                  leave-active-class="fade"
                  leave-to-class="">
        <div v-if="isVisible" class="modal-backdrop" :id="ids.backdrop"></div>
      </Transition>
    </div>
  </Teleport>
</template>

<script setup>
// Replaces bootstrap-vue's <b-modal> with the same markup and behaviour (only what the app uses, see
// __tests__/bootstrap/bModalUsage.test.js and bModal.test.js). Reference:
// node_modules/bootstrap-vue/src/components/modal/modal.js
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref, useSlots, watch } from "vue"
import { nextId } from "@/components/bootstrap/uid"
import { getModalManager } from "@/components/bootstrap/modalManager"
import { registerModal, unregisterModal } from "@/components/bootstrap/bvModal"

defineOptions({
  name: 'BModal',
  // plain attributes (e.g. data-model) go to the outer element, as in bootstrap-vue
  inheritAttrs: false
})

const props = defineProps({
  // open state (v-model)
  modelValue: {
    type: Boolean,
    default: false
  },
  id: {
    type: String,
    default: null
  },
  title: {
    type: String,
    default: null
  },
  size: {
    type: String,
    default: 'md'
  },
  centered: {
    type: Boolean,
    default: false
  },
  hideHeader: {
    type: Boolean,
    default: false
  },
  hideHeaderClose: {
    type: Boolean,
    default: false
  },
  hideFooter: {
    type: Boolean,
    default: false
  },
  noCloseOnBackdrop: {
    type: Boolean,
    default: false
  },
  noEnforceFocus: {
    type: Boolean,
    default: false
  },
  modalClass: {
    type: [String, Array, Object],
    default: null
  },
  contentClass: {
    type: [String, Array, Object],
    default: null
  },
  headerClass: {
    type: [String, Array, Object],
    default: null
  },
  titleClass: {
    type: [String, Array, Object],
    default: null
  },
  bodyClass: {
    type: [String, Array, Object],
    default: null
  },
  footerClass: {
    type: [String, Array, Object],
    default: null
  }
})

const emit = defineEmits(['update:modelValue', 'show', 'shown', 'hide', 'hidden', 'close'])

const slots = useSlots()
const manager = getModalManager()

// element ids as bootstrap-vue builds them: <id>___BV_modal_<part>_
const localId = nextId('modal')
const ids = computed(() => {
  const base = props.id || localId
  const part = (name) => `${base}___BV_modal_${name}_`
  return {
    modal: base,
    outer: part('outer'),
    header: part('header'),
    title: part('title'),
    body: part('body'),
    content: part('content'),
    footer: part('footer'),
    backdrop: part('backdrop')
  }
})

// the title is referenced by aria-labelledby only when the header shows one
const hasTitle = computed(() => !props.hideHeader && !!(slots['modal-title'] || props.title))

// the same states as bootstrap-vue: isHidden - not in the document (content not rendered),
// isVisible - shown, isOpening / isClosing - between the show / hide event and the end of the change,
// isTransitioning / isBlock / isShow - the fade
const isHidden = ref(true)
const isVisible = ref(false)
const isOpening = ref(false)
const isClosing = ref(false)
const isTransitioning = ref(false)
const isBlock = ref(false)
const isShow = ref(false)
const isModalOverflowing = ref(false)
// set by the modal manager while the modal is open (shared with bootstrap-vue's modals, see modalManager.js)
const managed = reactive({
  zIndex: manager.getBaseZIndex(),
  isTop: true,
  scrollbarWidth: 0,
  isBodyOverflowing: false
})

const modal = ref(null)
const content = ref(null)
const topTrap = ref(null)
const bottomTrap = ref(null)
let unmounted = false
let showWhenHidden = false
let returnFocus = null
let ignoreBackdropClick = false
let observer = null

// padding for the scrollbar, as bootstrap-vue
const modalStyles = computed(() => {
  const width = `${managed.scrollbarWidth}px`
  return {
    paddingLeft: !managed.isBodyOverflowing && isModalOverflowing.value ? width : '',
    paddingRight: managed.isBodyOverflowing && !isModalOverflowing.value ? width : '',
    display: isBlock.value ? 'block' : 'none'
  }
})

// the event object handlers receive (bootstrap-vue's BvModalEvent, with the fields the app can use)
const buildEvent = (type, { cancelable = false, trigger = null } = {}) => {
  let defaultPrevented = false
  return {
    type,
    cancelable,
    trigger,
    target: modal.value,
    componentId: ids.value.modal,
    preventDefault() {
      if (cancelable) defaultPrevented = true
    },
    get defaultPrevented() {
      return defaultPrevented
    }
  }
}

const updateModel = (value) => {
  if (value !== props.modelValue) emit('update:modelValue', value)
}

// --- focus ---

// tabbable elements, as bootstrap-vue's getTabables()
const TABBABLE = ['button', '[href]:not(.disabled)', 'input', 'select', 'textarea', '[tabindex]', '[contenteditable]']
  .map(selector => `${selector}:not(:disabled):not([disabled])`)
  .join(', ')
const isShown = (el) => {
  if (!document.body.contains(el) || getComputedStyle(el).display === 'none') return false
  const { height, width } = el.getBoundingClientRect()
  return height > 0 && width > 0
}
const tabbables = (root) => [...root.querySelectorAll(TABBABLE)].filter(isShown).filter(el => el.tabIndex > -1 && !el.disabled)

const activeElement = () => {
  const el = document.activeElement
  return el && el !== document.body && el.focus ? el : null
}

const attemptFocus = (el, options = {}) => {
  try {
    el.focus(options)
  } catch (e) {
    // element cannot take focus
  }
  return document.activeElement === el
}

// keeps focus inside the modal; only the top modal does it
const onFocusIn = (event) => {
  const target = event.target
  if (props.noEnforceFocus || !managed.isTop || !isVisible.value || !content.value ||
      target === document || content.value.contains(target)) {
    return
  }
  const elements = tabbables(content.value)
  if (bottomTrap.value && target === bottomTrap.value) {
    if (attemptFocus(elements[0])) return
  } else if (topTrap.value && target === topTrap.value) {
    if (attemptFocus(elements[elements.length - 1])) return
  }
  attemptFocus(content.value, { preventScroll: true })
}

const setEnforceFocus = (on) => {
  document.removeEventListener('focusin', onFocusIn)
  if (on) document.addEventListener('focusin', onFocusIn)
}

// focuses the modal unless something inside already has focus, and scrolls it to the top
const focusFirst = () => {
  requestAnimationFrame(() => {
    const active = activeElement()
    if (modal.value && content.value && !(active && content.value.contains(active))) {
      attemptFocus(content.value)
      nextTick(() => {
        if (modal.value) modal.value.scrollTop = 0
      })
    }
  })
}

const returnFocusTo = () => {
  const el = returnFocus
  returnFocus = null
  nextTick(() => {
    if (el) attemptFocus(el)
  })
}

// --- scrollbar ---

const checkModalOverflow = () => {
  if (isVisible.value && modal.value) {
    isModalOverflowing.value = modal.value.scrollHeight > document.documentElement.clientHeight
  }
}

const setResizeEvent = (on) => {
  window.removeEventListener('resize', checkModalOverflow)
  window.removeEventListener('orientationchange', checkModalOverflow)
  if (on) {
    window.addEventListener('resize', checkModalOverflow)
    window.addEventListener('orientationchange', checkModalOverflow)
  }
}

// rechecks the overflow when the content changes
const setObserver = (on) => {
  observer?.disconnect()
  observer = null
  if (on && content.value) {
    observer = new MutationObserver(checkModalOverflow)
    observer.observe(content.value, {
      subtree: true,
      childList: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['style', 'class']
    })
  }
}

// --- open / close ---

const show = () => {
  if (isVisible.value || isOpening.value) return
  if (isClosing.value) {
    // opened again while closing: open once it is hidden
    showWhenHidden = true
    return
  }
  isOpening.value = true
  returnFocus = returnFocus || activeElement()
  const event = buildEvent('show', { cancelable: true })
  emit('show', event)
  if (unmounted) return
  if (event.defaultPrevented || isVisible.value) {
    isOpening.value = false
    updateModel(false)
    return
  }
  manager.registerModal(managed)
  // place the modal in the document first, show it on the next tick
  isHidden.value = false
  nextTick(() => {
    if (unmounted) return
    isVisible.value = true
    isOpening.value = false
    updateModel(true)
    nextTick(() => setObserver(true))
  })
}

const hide = (trigger = '') => {
  if (!isVisible.value || isClosing.value) return
  isClosing.value = true
  const event = buildEvent('hide', { cancelable: trigger !== 'FORCE', trigger: trigger || null })
  if (trigger === 'headerclose') emit('close', event)
  emit('hide', event)
  // a handler may unmount the modal (FormModal does): then nothing more happens, as in bootstrap-vue
  if (unmounted) return
  if (event.defaultPrevented || !isVisible.value) {
    isClosing.value = false
    updateModel(true)
    return
  }
  setObserver(false)
  isVisible.value = false
  updateModel(false)
}

// --- transition hooks ---
// The modal's <Transition> has no classes: the hooks set display at enter and `show` two frames later, as in
// bootstrap-vue. The backdrop's <Transition> uses the classes bootstrap-vue's BVTransition ended up with in the app:
// `fade` while changing, `show` at the end of entering.

const onBeforeEnter = () => {
  isTransitioning.value = true
  setResizeEvent(true)
}

const onEnter = () => {
  isBlock.value = true
  // `show` one frame later: two requestAnimationFrame calls make sure that frame has been rendered
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      isShow.value = true
    })
  })
}

const onAfterEnter = () => {
  checkModalOverflow()
  isTransitioning.value = false
  requestAnimationFrame(() => {
    if (unmounted || !isVisible.value) return
    emit('shown', buildEvent('shown'))
    setEnforceFocus(true)
    nextTick(focusFirst)
  })
}

const onBeforeLeave = () => {
  isTransitioning.value = true
  setResizeEvent(false)
  setEnforceFocus(false)
}

const onLeave = () => {
  isShow.value = false
}

const onAfterLeave = () => {
  if (unmounted) return
  isBlock.value = false
  isTransitioning.value = false
  isModalOverflowing.value = false
  isHidden.value = true
  nextTick(() => {
    if (unmounted) return
    isClosing.value = false
    manager.unregisterModal(managed)
    returnFocusTo()
    emit('hidden', buildEvent('hidden'))
    if (showWhenHidden) {
      showWhenHidden = false
      show()
    }
  })
}

// --- closing triggers ---

// header close button and the `close` of the #modal-footer slot
const onClose = () => hide('headerclose')

const onEsc = (event) => {
  if (event.keyCode === 27 && isVisible.value) hide('esc')
}

// a press that starts in the dialog and ends outside it is not a backdrop click
const onDialogMousedown = () => {
  const el = modal.value
  const onMouseup = (event) => {
    el.removeEventListener('mouseup', onMouseup)
    if (event.target === el) ignoreBackdropClick = true
  }
  el.addEventListener('mouseup', onMouseup)
}

// the .modal element covers the page around the dialog: a click there is a backdrop click
const onClickOut = (event) => {
  if (ignoreBackdropClick) {
    ignoreBackdropClick = false
    return
  }
  if (!isVisible.value || props.noCloseOnBackdrop || !document.body.contains(event.target)) return
  if (!content.value.contains(event.target)) hide('backdrop')
}

// reachable by $bvModal.show(id) / .hide(id) (bvModal.js) under its current id
const api = { show, hide }
watch(() => ids.value.modal, (id, oldId) => {
  unregisterModal(oldId, api)
  registerModal(id, api)
})

watch(() => props.modelValue, (value, oldValue) => {
  if (value !== oldValue) value ? show() : hide()
})

onMounted(() => {
  managed.zIndex = manager.getBaseZIndex()
  registerModal(ids.value.modal, api)
  if (props.modelValue) nextTick(show)
})

onBeforeUnmount(() => {
  unmounted = true
  unregisterModal(ids.value.modal, api)
  manager.unregisterModal(managed)
  setObserver(false)
  setEnforceFocus(false)
  setResizeEvent(false)
})

defineExpose({
  show,
  hide
})
</script>
